import { css } from '@emotion/react'
import styled from '@emotion/styled'

const Button = styled.button<{ isActive: boolean; isComplete: boolean }>(
  ({ isActive, isComplete, theme: { colors, spacing, zIndex } }) => [
    css`
      background: ${colors.neutral1};
      border-color: ${colors.neutral1};
      border-radius: 100%;
      border-style: solid;
      border-width: ${spacing.x1}px;
      height: ${spacing.x4}px;
      position: relative;
      transition: all ease-in-out 300ms;
      width: ${spacing.x4}px;
      z-index: ${zIndex.relative2};

      :disabled {
        background: ${colors.neutral1};
        cursor: auto;
      }
    `,
    isActive &&
      css`
        border-color: ${colors.primaryPurple};
      `,
    isComplete &&
      css`
        background-color: ${colors.primaryPurple};
        border-color: ${colors.primaryPurple};
        cursor: pointer;

        :hover {
          border-color: ${colors.primaryPurpleDark};
          background: ${colors.primaryPurpleDark};
        }
      `,
  ],
)

const Heading = styled.h3<{ active: boolean }>(
  ({ active, theme: { breakpoints, colors, fonts, spacing } }) => css`
    color: ${colors.neutral4};
    display: ${active ? 'block' : 'none'};
    font-size: 30px;
    left: 0;
    position: absolute;
    top: 0;
    white-space: nowrap;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      display: block;
      font-family: ${fonts.inter.style.fontFamily}, sans-serif;
      font-size: 14px;
      font-weight: normal;
      left: calc(-50% + ${spacing.x4 / 2}px);
      text-align: center;
      width: 100%;
    }
  `,
)

const List = styled.ol<{ activePercentage: number }>(
  ({ activePercentage, theme: { colors } }) =>
    css`
      background: ${colors.neutral0};
      display: flex;
      list-style-type: none;
      margin: 0;
      padding: 0;
      position: relative;

      ::after,
      ::before {
        background: ${colors.neutral1};
        bottom: 14px;
        content: '';
        height: 6px;
        left: 0;
        right: 0;
        position: absolute;
        transition: right 300ms ease-in-out;
      }
      ::after {
        background: ${colors.primaryPurple};
        right: ${Math.max(0, 100 - activePercentage)}%;
      }
      :last-of-type {
        flex: 0;
      }
    `,
)

const ListItem = styled.li(
  ({ theme: { breakpoints, spacing } }) => css`
    flex: 1;
    padding-top: ${spacing.x8 - spacing.x0}px;

    :last-of-type {
      flex: 0;
    }

    @media screen and (min-width: ${breakpoints.tablet}px) {
      position: relative;
    }

    @media screen and (min-width: ${breakpoints.tablet}px) {
      :first-of-type {
        ${Heading} {
          left: 0;
          text-align: left;
          width: auto;
        }
      }
      :last-of-type {
        ${Heading} {
          left: auto;
          right: 0;
          text-align: right;
          width: auto;
        }
      }
    }
  `,
)

interface Props {
  active: number
  steps: { id: string; title: string }[]
  onClick?: (step: number) => void
}

const Stepper = ({ active, steps, onClick }: Props) => {
  const activePercentage = (active * 100) / (steps.length - 1)
  return (
    <List activePercentage={activePercentage} role="tablist">
      {steps.map(({ id, title }, index) => (
        <ListItem key={id}>
          <Heading
            active={
              active === index ||
              (active === steps.length && index === steps.length - 1)
            }
          >
            {title}
          </Heading>
          <Button
            aria-controls={id}
            aria-disabled={active < index}
            aria-label={title}
            aria-selected={active === index}
            id={`tab-${id}`}
            disabled={active < index}
            isActive={active === index}
            isComplete={active > index}
            onClick={() => onClick && onClick(index)}
            role="tab"
            type="button"
          />
        </ListItem>
      ))}
    </List>
  )
}

export default Stepper
